import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  wia_gold_1m: {
    label: 'GOLD',
    duration_label: '1 mês',
    accent: '#d4a94a',
    gradient: 'linear-gradient(135deg,#7a5a22 0%,#3a2a0f 100%)',
    bullets: ['WhatsApp · Telegram · iMessage', 'Ideal para experimentar', 'Renovação livre'],
  },
  wia_black_6m: {
    label: 'BLACK',
    duration_label: '6 meses',
    accent: '#c9c9c9',
    gradient: 'linear-gradient(135deg,#151515 0%,#000 100%)',
    bullets: ['Todos os canais', 'Economia de 17%', 'Renovação automática opcional'],
    featured: true,
  },
  wia_diamond_12m: {
    label: 'DIAMOND',
    duration_label: '12 meses',
    accent: '#7ad8e3',
    gradient: 'linear-gradient(135deg,#082234 0%,#000 100%)',
    bullets: ['Todos os canais', 'Melhor valor · 17% off', 'Prioridade na fila'],
  },
};

export default function WIAActivate() {
  const [plans, setPlans] = useState([]);
  const [phone, setPhone] = useState('+55');
  const [loading, setLoading] = useState(null); // lookup_key while redirecting
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/wia/plans`).then(r => setPlans(r.data?.plans || [])).catch(() => setPlans([]));
  }, []);

  const formatPrice = (amount, currency) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: (currency || 'BRL').toUpperCase() })
      .format((amount || 0) / 100);

  const startCheckout = async (plan) => {
    setError(null);
    const clean = phone.replace(/[^\d+]/g, '');
    if (clean.length < 11) {
      setError('Digite um telefone válido (formato +55XXXXXXXXXXX)');
      return;
    }
    setLoading(plan.lookup_key);
    try {
      const res = await axios.post(`${API}/wia/checkout`, {
        plan_key: plan.lookup_key,
        phone: clean,
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setLoading(null);
        setError('Falha ao iniciar checkout — tente novamente.');
      }
    } catch (e) {
      setLoading(null);
      setError(e?.response?.data?.detail || 'Erro ao criar sessão de pagamento.');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Ambient */}
      <div className="fixed inset-0 pointer-events-none" style={{
        background: 'radial-gradient(60% 40% at 50% 0%, rgba(122,216,227,0.08), transparent 70%)',
      }} />

      <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-24" data-testid="wia-activate-page">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/20 bg-cyan-400/5 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-[10px] tracking-widest font-['Jost'] text-cyan-400/80">W IA · ATIVAR ACESSO</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-4">
            Conversar com o W
          </h1>
          <p className="text-white/50 text-base max-w-xl mx-auto font-['Jost'] leading-relaxed">
            Autenticação de charutos, vinhos e whiskies pelo seu WhatsApp, Telegram e iMessage.
            Ative com o seu número — cancele quando quiser.
          </p>
        </div>

        {/* Phone input */}
        <div className="max-w-md mx-auto mb-10">
          <label className="block text-white/40 text-[10px] tracking-widest font-['Jost'] mb-2">
            SEU TELEFONE (com DDD e país)
          </label>
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            data-testid="wia-phone-input"
            placeholder="+55 47 99953-7338"
            className="w-full px-5 py-4 rounded-2xl bg-white/[0.04] border border-white/10 text-white text-lg font-['Space_Mono'] tabular-nums placeholder-white/20 focus:outline-none focus:border-cyan-400/40 focus:bg-white/[0.06] transition"
          />
          <p className="text-white/30 text-[11px] mt-2 font-['Jost']">
            Este número receberá o acesso ao W IA nas redes.
          </p>
          {error && (
            <p className="text-rose-400 text-xs mt-3 font-['Jost']" data-testid="wia-error">{error}</p>
          )}
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan, i) => {
            const meta = TIER_META[plan.lookup_key] || {};
            const isFeatured = meta.featured;
            return (
              <motion.div
                key={plan.lookup_key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className={`relative rounded-3xl p-6 border transition ${
                  isFeatured ? 'border-white/30' : 'border-white/10 hover:border-white/20'
                }`}
                style={{
                  background: meta.gradient || 'linear-gradient(135deg,rgba(255,255,255,.03),rgba(255,255,255,.01))',
                  backdropFilter: 'blur(20px)',
                }}
                data-testid={`wia-plan-${plan.lookup_key}`}
              >
                {isFeatured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white text-black text-[10px] tracking-widest font-medium">
                    MAIS ESCOLHIDO
                  </div>
                )}
                <div
                  className="text-[10px] tracking-[0.3em] font-['Jost'] mb-1"
                  style={{ color: meta.accent }}
                >
                  W IA · {meta.label}
                </div>
                <div className="text-white/50 text-xs font-['Jost'] mb-5">{meta.duration_label}</div>

                <div className="mb-6">
                  <div className="text-4xl font-light text-white tabular-nums">
                    {formatPrice(plan.amount, plan.currency)}
                  </div>
                  <div className="text-white/40 text-[11px] font-['Jost'] mt-1">
                    Pagamento único · {plan.duration_days} dias de acesso
                  </div>
                </div>

                <ul className="space-y-2 mb-8">
                  {(meta.bullets || []).map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-white/60 text-sm font-['Jost']">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: meta.accent }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <button
                  disabled={loading !== null}
                  onClick={() => startCheckout(plan)}
                  data-testid={`wia-checkout-${plan.lookup_key}`}
                  className="w-full py-3 rounded-full text-sm tracking-wide font-medium transition disabled:opacity-40"
                  style={{
                    background: isFeatured ? '#fff' : `${meta.accent}22`,
                    color: isFeatured ? '#000' : meta.accent,
                    border: isFeatured ? 'none' : `1px solid ${meta.accent}44`,
                  }}
                >
                  {loading === plan.lookup_key ? 'Abrindo Stripe…' : 'ATIVAR AGORA'}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="text-center mt-14 text-white/30 text-xs font-['Jost']">
          Pagamento processado pelo Stripe · Cancele por email quando quiser
          <div className="mt-3">
            <button onClick={() => navigate('/')} className="text-white/40 hover:text-white/70 transition underline underline-offset-4 text-xs">
              Já tem plano? Volte ao WClub
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
